/**
 * Shared Transformation Utilities
 *
 * Centralized helpers for transformation type checking, classification, and utilities.
 * Used across the application to ensure consistency in how transformations are identified and displayed.
 */

/**
 * Check if a transformation type is AI-powered
 * This includes standard AI, external AI, and AI analysis features
 */
export function isAITransformation(type: string): boolean {
  const aiTransformations = [
    // Standard AI transformations (Cloudinary AI)
    'background_removal',
    'ai_upscale',
    'generative_fill',
    'generative_remove',
    'generative_replace',
    'generative_background_replace',
    'quality_enhancement',

    // External AI (BFL FLUX API)
    'invisible_mannequin',
    'object_placement',

    // External AI Pro (Gemini 2.5 Flash API)
    'invisible_mannequin_pro',
    'object_placement_pro',

    // AI Analysis
    'auto_tagging',
    'object_detection',
    'auto_enhancement',
    'noise_reduction',
    'auto_color',
    'auto_brightness'
  ];

  return aiTransformations.includes(type);
}

/**
 * Check if a transformation is an external AI service (separate processing pipeline)
 */
export function isExternalAI(type: string): boolean {
  return type === 'invisible_mannequin' ||
         type === 'object_placement' ||
         type === 'invisible_mannequin_pro' ||
         type === 'object_placement_pro';
}

/**
 * Check if a transformation is a standard Cloudinary AI transformation
 */
export function isStandardAI(type: string): boolean {
  return isAITransformation(type) && !isExternalAI(type);
}

/**
 * Get the visual style class for a transformation badge
 * Returns Tailwind classes for consistent styling across the app
 */
export function getTransformationBadgeClass(type: string): string {
  if (type === 'invisible_mannequin') {
    return 'bg-purple-100 dark:bg-purple-950/50 text-purple-800 dark:text-purple-200 border-purple-300 dark:border-purple-700 font-semibold';
  }

  if (type === 'invisible_mannequin_pro') {
    return 'bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-950/50 dark:to-pink-950/50 text-purple-900 dark:text-purple-100 border-purple-400 dark:border-purple-600 font-bold';
  }

  if (type === 'object_placement') {
    return 'bg-amber-100 dark:bg-amber-950/50 text-amber-800 dark:text-amber-200 border-amber-300 dark:border-amber-700 font-semibold';
  }

  if (type === 'object_placement_pro') {
    return 'bg-gradient-to-r from-amber-100 to-orange-100 dark:from-amber-950/50 dark:to-orange-950/50 text-amber-900 dark:text-amber-100 border-amber-400 dark:border-amber-600 font-bold';
  }

  if (isStandardAI(type)) {
    return 'bg-blue-100 dark:bg-blue-950/50 text-blue-800 dark:text-blue-200 border-blue-200 dark:border-blue-800';
  }

  return ''; // Default styling from variant
}

/**
 * Get the transformation category for classification
 */
export function getTransformationCategory(type: string): 'external_ai' | 'standard_ai' | 'basic' {
  if (isExternalAI(type)) {
    return 'external_ai';
  }

  if (isStandardAI(type)) {
    return 'standard_ai';
  }

  return 'basic';
}

/**
 * Check if a preset contains any AI transformations
 */
export function presetHasAIFeatures(transformations: Array<{ type: string }>): boolean {
  return transformations.some(t => isAITransformation(t.type));
}

/**
 * Get the background class for a preset card based on its transformations
 */
export function getPresetCardClass(key: string, transformations: Array<{ type: string }>): string {
  const isGhostMannequin = key === 'invisible_mannequin';
  const isGhostMannequinPro = key === 'invisible_mannequin_pro';
  const isObjectPlacement = key === 'object_placement';
  const isObjectPlacementPro = key === 'object_placement_pro';
  const hasAIFeatures = presetHasAIFeatures(transformations);

  if (isGhostMannequin) {
    return 'bg-gradient-to-br from-purple-50 via-pink-50 to-purple-50 dark:from-purple-950/30 dark:via-pink-950/30 dark:to-purple-950/30 border-purple-300 dark:border-purple-700 hover:shadow-xl hover:shadow-purple-200/50 dark:hover:shadow-purple-900/50 ring-2 ring-purple-200 dark:ring-purple-800';
  }

  if (isGhostMannequinPro) {
    return 'bg-gradient-to-br from-purple-100 via-pink-100 to-fuchsia-100 dark:from-purple-900/40 dark:via-pink-900/40 dark:to-fuchsia-900/40 border-purple-400 dark:border-purple-600 hover:shadow-2xl hover:shadow-purple-300/60 dark:hover:shadow-purple-800/60 ring-2 ring-purple-300 dark:ring-purple-700';
  }

  if (isObjectPlacement) {
    return 'bg-gradient-to-br from-amber-50 via-orange-50 to-amber-50 dark:from-amber-950/30 dark:via-orange-950/30 dark:to-amber-950/30 border-amber-300 dark:border-amber-700 hover:shadow-xl hover:shadow-amber-200/50 dark:hover:shadow-amber-900/50 ring-2 ring-amber-200 dark:ring-amber-800';
  }

  if (isObjectPlacementPro) {
    return 'bg-gradient-to-br from-amber-100 via-orange-100 to-yellow-100 dark:from-amber-900/40 dark:via-orange-900/40 dark:to-yellow-900/40 border-amber-400 dark:border-amber-600 hover:shadow-2xl hover:shadow-amber-300/60 dark:hover:shadow-amber-800/60 ring-2 ring-amber-300 dark:ring-amber-700';
  }

  if (hasAIFeatures) {
    return 'bg-gradient-to-br from-blue-50 to-purple-50 border-blue-200 dark:from-blue-950/30 dark:to-purple-950/30 dark:border-blue-800 hover:bg-accent/50 dark:hover:bg-accent/30';
  }

  return 'border-border hover:border-border/80 dark:border-gray-700 dark:hover:border-gray-600 hover:bg-accent/50 dark:hover:bg-accent/30';
}

/**
 * Get text color class for preset descriptions
 */
export function getPresetDescriptionClass(key: string): string {
  if (key === 'invisible_mannequin' || key === 'invisible_mannequin_pro') {
    return 'text-purple-700 dark:text-purple-300';
  }

  if (key === 'object_placement' || key === 'object_placement_pro') {
    return 'text-amber-700 dark:text-amber-300';
  }

  return 'text-muted-foreground dark:text-gray-400';
}

/**
 * Get title text color class for presets
 */
export function getPresetTitleClass(key: string): string {
  if (key === 'invisible_mannequin' || key === 'invisible_mannequin_pro') {
    return 'text-lg text-purple-900 dark:text-purple-100';
  }

  if (key === 'object_placement' || key === 'object_placement_pro') {
    return 'text-lg text-amber-900 dark:text-amber-100';
  }

  return 'text-gray-900 dark:text-gray-100';
}
